import React, { useState, useEffect, useRef } from 'react';
import { FiSearch, FiX } from 'react-icons/fi';
import { MdPayments } from 'react-icons/md';
import { paymentService } from '../services';
import type { Service } from '../types';

interface PaymentSearchBarProps {
  onServiceSelect: (service: Service) => void;
}

const PaymentSearchBar: React.FC<PaymentSearchBarProps> = ({ onServiceSelect }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Service[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout>();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (query.length < 2) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      const searchResults = await paymentService.searchServices(query);
      setResults(searchResults);
      setIsOpen(true);
      setLoading(false);
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query]);

  const handleSelect = (service: Service) => {
    onServiceSelect(service);
    setQuery('');
    setIsOpen(false);
  };

  const handleClear = () => {
    setQuery('');
    setResults([]);
    setIsOpen(false);
  };

  const getServiceName = (s: Service) => s.nome || s.NOME || '';
  const getServiceLogo = (s: Service) => {
    const logo = s.logo || s.LOGO;
    if (!logo) return undefined;
    if (typeof logo === 'string') return logo;
    if (logo.type === 'Buffer' && Array.isArray(logo.data)) {
      let binary = '';
      for (let i = 0; i < logo.data.length; i++) {
        binary += String.fromCharCode(logo.data[i]);
      }
      return btoa(binary);
    }
    return undefined;
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-pagali-gray-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Pesquisar serviços..."
          className="w-full pl-11 pr-10 py-3 bg-white border border-pagali-gray-200 rounded-xl
                     focus:outline-none focus:ring-2 focus:ring-pagali-primary/20 focus:border-pagali-primary
                     text-pagali-gray-800 placeholder:text-pagali-gray-400"
        />
        {query && (
          <button
            onClick={handleClear}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-pagali-gray-400 hover:text-pagali-gray-600"
          >
            <FiX />
          </button>
        )}
      </div>

      {/* Search Results Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-pagali-gray-100 z-50 max-h-80 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center text-pagali-gray-500">
              A pesquisar...
            </div>
          ) : results.length === 0 ? (
            <div className="p-4 text-center text-pagali-gray-500">
              Nenhum serviço encontrado
            </div>
          ) : (
            <div className="py-2">
              {results.map((service) => {
                const logo = getServiceLogo(service);
                return (
                  <button
                    key={service.id || service.ID}
                    onClick={() => handleSelect(service)}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-pagali-gray-50 transition-colors text-left"
                  >
                    {logo ? (
                      <img
                        src={`data:${service.mimeType || 'image/png'};base64,${logo}`}
                        alt=""
                        className="w-10 h-10 rounded-lg object-contain bg-pagali-gray-50 p-1"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-pagali-gray-100 flex items-center justify-center">
                        <MdPayments className="text-pagali-gray-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-pagali-gray-800 truncate">
                        {getServiceName(service)}
                      </p>
                      <p className="text-sm text-pagali-gray-500 truncate">
                        {service.entidade || ''}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PaymentSearchBar;
